import { say } from "../../words.ts";

/**
 * The character window's tab registry (ADR-138's amendment of 2026-10-31, decision 2): each tab an entry of an id, its
 * label key and whether it shows on the page's form; the window draws every visible tab and a later system adds an
 * entry here and its page in `pages.tsx` without touching the frame.
 */

/**
 * A tab's id.
 */
export type CharacterTabId = "character" | "companions" | "equipment" | "skills" | "titles";

/**
 * What decides a tab's visibility: whether the window is drawn in its phone form.
 */
export interface TabContext {
  readonly phone: boolean;
}

/**
 * One tab: its id, its label, whether it shows and whether its page is a skeleton ("Not yet.").
 */
export interface CharacterTab {
  readonly id: CharacterTabId;
  readonly label: () => string;
  readonly visible: (context: TabContext) => boolean;
  readonly skeleton: boolean;
}

/**
 * Every tab in order: Character, Companions, Equipment (phones only; desktop keeps its own window), Skills and Titles.
 */
export const CHARACTER_TABS: readonly CharacterTab[] = Object.freeze([
  { id: "character", label: () => say("character.tabs.character"), visible: () => true, skeleton: false },
  { id: "companions", label: () => say("character.tabs.companions"), visible: () => true, skeleton: false },
  { id: "equipment", label: () => say("character.tabs.equipment"), visible: (context) => context.phone, skeleton: true },
  { id: "skills", label: () => say("character.tabs.skills"), visible: () => true, skeleton: true },
  { id: "titles", label: () => say("character.tabs.titles"), visible: () => true, skeleton: true },
]);

/**
 * The tabs a form shows.
 *
 * @param context The form.
 * @returns The visible tabs, in order.
 */
export function visibleTabs(context: TabContext): CharacterTab[] {
  return CHARACTER_TABS.filter((tab) => tab.visible(context));
}
